import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Component that ensures all URLs (except root) have trailing slashes.
 * This matches Django's APPEND_SLASH behavior for consistency.
 * 
 * Benefits:
 * - Consistent with Django's URL patterns
 * - Better SEO (canonical URLs)
 * - Prevents duplicate content issues
 * - Matches server-side behavior
 */
const TrailingSlashRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search, hash } = location;

    // Don't redirect root path
    if (pathname === '/') {
      return;
    }

    // Check if pathname doesn't end with a slash
    // Also exclude file extensions (like .json, .png, .js, .css, etc.)
    const hasFileExtension = /\.[a-zA-Z0-9]+$/.test(pathname);
    
    // Don't redirect if it's a file or already has trailing slash
    if (hasFileExtension || pathname.endsWith('/')) {
      return;
    }

    // Redirect to the same path with trailing slash
    // Using replace: true prevents adding to history and avoids loops
    const newPath = pathname + '/' + search + hash;
    navigate(newPath, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  return <>{children}</>;
};

export default TrailingSlashRedirect;

